// Library methods
import { useMemo, useState } from "react";
import { useTranslation } from "react-i18next";

// Components
import Grid from "@mui/material/Grid";
import Typography from "@mui/material/Typography";
import UpdateDeviceModal from "./UpdateDeviceModal/UpdateDeviceModal";

// Contexts
import { useDevicesContext } from "src/contexts/devices/DevicesContext";

// Utils
import { stringContainsNumber } from "src/utils/number/numberHelper";

const DeviceVersionBanner = () => {
  const { t } = useTranslation();
  const { devices, isLoadingDevices, isErrorDevices } = useDevicesContext();

  // get the minimum compatible version of all devices
  // default return value: null
  const minCompatibleVersion: string | null = useMemo(() => {
    // sort devices by their minimum compatible version
    const sortedDevices = devices
      ?.filter(
        (device) =>
          device?.minimumCompatibleVersion &&
          stringContainsNumber(device?.minimumCompatibleVersion)
      )
      ?.sort(
        (a, b) =>
          parseFloat(a.minimumCompatibleVersion) -
          parseFloat(b.minimumCompatibleVersion)
      );

    return sortedDevices?.[0]?.minimumCompatibleVersion ?? null;
  }, [devices]);

  // show the banner if at least one device isn't compatible
  // and the device data has finished being loaded
  const showVersionBanner: boolean = useMemo(
    () =>
      Boolean(
        devices?.length &&
          devices?.some((device) => !device?.webserviceCompatible) &&
          !isLoadingDevices &&
          !isErrorDevices
      ),
    [devices, isLoadingDevices, isErrorDevices]
  );

  const [updateModal, setUpdateModal] = useState(false);

  return (
    <Grid
      item
      bgcolor="#E7C43C"
      width="100%"
      padding="8px"
      display={showVersionBanner ? "flex" : "none"}
      alignItems="center"
      justifyContent="center"
    >
      <Typography
        color="#000"
        textAlign="center"
        fontSize={{ xs: "12px", sm: "14px" }}
      >
        <span>
          {minCompatibleVersion
            ? t(
                "Version {{ versionNum }} is ready. Please make sure VR headset is connected to Wi-Fi to update.",
                { versionNum: minCompatibleVersion }
              )
            : t(
                "New version is ready. Please make sure VR headset is connected to Wi-Fi to update."
              )}
        </span>
        <span
          role="button"
          onClick={() => setUpdateModal(true)}
          style={{
            marginLeft: "6px",
            textDecoration: "underline",
            fontWeight: 800,
            cursor: "pointer",
          }}
        >
          {t("Learn more")}
        </span>
      </Typography>

      <UpdateDeviceModal
        open={updateModal}
        onClose={() => setUpdateModal(false)}
      />
    </Grid>
  );
};

export default DeviceVersionBanner;
